import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

import api from "../../lib/api";
import AdminSidebar from "./AdminSidebar";
import AdminHeader from "./AdminHeader";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { AdminUserContext } from "../../context/AdminUserContext";


// The "user" role may only reach Blogs pages (mirrors the sections shown in
// AdminSidebar). Admins and any other role get full access.
const isUserAllowedPath = (pathname) => pathname.startsWith("/admin/blog");

// A freshly created user still has the backend's default password. The flag
// may arrive as true, 1 or "1".
const isDefaultUser = (user) =>
  user?.is_default === true ||
  user?.is_default === 1 ||
  user?.is_default === "1";

const AdminLayout = ({ title = "Admin Panel", subtitle, actions, children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Drives the change-password dialog opened from the default-user prompt.
  const [changePwOpen, setChangePwOpen] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const { data } = await api.get("/check-auth");

        if (data && (data.status === true || data.authenticated === true)) {
          const resolvedUser =
            data.user || JSON.parse(localStorage.getItem("user") || "null");

          // Block "user" role from non-Blogs pages reached by URL.
          const role = (resolvedUser?.role || "").toLowerCase();
          if (role === "user" && !isUserAllowedPath(pathname)) {
            navigate("/admin/blogs", { replace: true });
            return;
          }

          setUser(resolvedUser);
          setLoading(false);
        } else {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } catch (error) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    };

    checkAuth();
  }, [navigate, pathname]);

  const handleLogout = async () => {
    try {
      await api.post("/logout");
    } catch (error) {
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      toast.success("Logged out successfully");
      navigate("/login");
    }
  };

  // After a successful password change the user no longer has the default
  // password — clear the flag so the prompt stops appearing. Mirror it into
  // the stored user so a refresh doesn't bring the prompt back.
  const handlePasswordChanged = () => {
    setUser((prev) => (prev ? { ...prev, is_default: false } : prev));
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      if (stored) {
        localStorage.setItem(
          "user",
          JSON.stringify({ ...stored, is_default: false })
        );
      }
    } catch (e) {
      /* ignore malformed stored user */
    }
  };

  const mustChangePassword = isDefaultUser(user);

  // if (loading) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-600">
  //       <Loader2 className="h-5 w-5 animate-spin mr-2" />
  //       Checking authentication...
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen flex bg-gray-100">
      <AdminSidebar
        open={sidebarOpen}
        user={user}
        onLogout={handleLogout}
        onPasswordChanged={handlePasswordChanged}
      />

      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
          sidebarOpen ? "ml-64" : "ml-20"
        }`}
      >
        <AdminHeader
          title={title}
          subtitle={subtitle}
          actions={actions}
          onToggleSidebar={() => setSidebarOpen((v) => !v)}
        />
        <main className="flex-1 p-6">
          {/* Default-password reminder — stays visible until the user sets
              their own password, but does not block using the rest of the
              admin panel. */}
          {mustChangePassword && (
            <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <KeyRound className="h-5 w-5 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  You're using a default password
                </p>
                <p className="text-sm text-amber-700">
                  Please update your password to a new one.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setChangePwOpen(true)}
                className="shrink-0 rounded-md bg-[#b32126] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#8f1a1f] transition-colors"
              >
                Update Password
              </button>
            </div>
          )}

          <AdminUserContext.Provider value={user}>
            {children}
          </AdminUserContext.Provider>
        </main>
      </div>

      <ChangePasswordDialog
        open={changePwOpen}
        onOpenChange={setChangePwOpen}
        user={user}
        onSuccess={handlePasswordChanged}
      />
    </div>
  );
};

export default AdminLayout;
